import React, { useEffect, useState } from "react";
import { observer } from "mobx-react-lite";
import { withRouter } from "react-router";
import {
  Grid, Box, Paper, List, ListItem, ListItemAvatar, ListItemText, ListItemSecondaryAction,
  IconButton, Tooltip, Accordion, AccordionSummary, AccordionDetails, Typography, MenuItem, useTheme, useMediaQuery, Divider
} from "@mui/material";
import { KeyboardArrowLeft, KeyboardArrowRight, AssignmentOutlined, ExpandMore, Check } from "@mui/icons-material";

import { getSiteHoursReport, getSiteSubscription } from '../../../actions/internal/SiteActions';
import { formatDateToFancyString } from '../../../utilities/DateUtilities';
import Colors from '../../../constants/Colors';
import StyledPage from '../../common/StyledPage';
import StyledSubHeader from '../../common/StyledSubHeader';
import StyledSection from '../../common/StyledSection';
import StyledStatusIcon from '../../common/StyledStatusIcon';
import StyledProgressCircle from '../../common/StyledProgressCircle';
import { TooltipIconButton } from "../../common/StyledButtons";
import ViewRequestModal from '../modals/ViewRequestModal';
import { getSiteById } from "../../../selectors/SiteSelectors";
import { getUserId } from "../../../selectors/UserSelectors";
import { StyledDropDown } from "../../common/StyledDropDown";
import StyledAccordion, { StyledAccordionItem } from "../../common/StyledAccordion";

const SupportCompletedPage = ({ match }) => {
  const siteId = match.params.site_id;

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [offset, setOffset] = useState(0);
  const [hoursReport, setHoursReport] = useState();
  const [prevCycleReport, setPrevCycleReport] = useState();
  
  const [selectedOffset, setSelectedOffset] = useState(0);
  const [historyReport, setHistoryReport] = useState();
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);

  const [subscription, setSubscription] = useState();
  const [isLoading, setIsLoading] = useState(true);
  const [requestInView, setRequestInView] = useState();

  const site = getSiteById(siteId);

  useEffect(() => {
    const loadAllReports = async () => {
      setIsLoading(true);
      try {
        const userId = getUserId();

        const current = await getSiteHoursReport(siteId, 0);
        const previous = await getSiteHoursReport(siteId, -1);
        const subscription = await getSiteSubscription(userId, siteId)
        
        setSubscription(subscription);
        setHoursReport(current);
        setPrevCycleReport(previous);
      } catch (err) {
        console.error(err);
      } finally {
        setIsLoading(false);
      }
    };
    loadAllReports();
  }, [siteId]);

  useEffect(() => {
    const fetchHistory = async () => {
      setIsLoadingHistory(true);
      try {
        const result = await getSiteHoursReport(siteId, selectedOffset);
        setHistoryReport(result);
      } catch (err) {
        console.error('Error loading historical report:', err);
      } finally {
        setIsLoadingHistory(false);
      }
    };

    if (selectedOffset === 0) return;
    fetchHistory();
  }, [selectedOffset, siteId]);

  const summaryBoxStyles = {
    padding: '12px',
    minHeight: isMobile ? 'auto' : '100px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
  };
  
  const summaryTitle = { fontSize: '14px', marginBottom: '4px', marginTop: 0 };
  const summaryValue = { fontSize: '24px', margin: 0 };  

  const renderCycleHeader = (data) => {
    const requests = (data && Array.isArray(data.tracked_completed_requests)) ? data.tracked_completed_requests : [];
    const totalHours = data && data.time_utilized && data.time_utilized.total_time_hours ? data.time_utilized.total_time_hours : "0.00";
    const cycleEnd = data && data.time_period && data.time_period.end
      ? new Date(data.time_period.end).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })
      : "-";
  
    return (
      <Grid container spacing={2}>
        <Grid item xs={12} sm={4}>
          <Box sx={summaryBoxStyles}>
            <h4 style={summaryTitle}>Total Hours Completed</h4>
            <h3 style={summaryValue}>{totalHours} hrs</h3>
          </Box>
          {isMobile && <Divider />}
        </Grid>
        <Grid item xs={12} sm={4}>
          <Box sx={summaryBoxStyles}>
            <h4 style={summaryTitle}>Cycle End Date</h4>
            <h3 style={summaryValue}>{cycleEnd}</h3>
          </Box>
          {isMobile && <Divider />}
        </Grid>
        <Grid item xs={12} sm={4}>
          <Box sx={summaryBoxStyles}>
            <h4 style={summaryTitle}>Requests Tracked</h4>
            <h3 style={summaryValue}>{requests.length}</h3>
          </Box>
        </Grid>
      </Grid>
    );
  };

  const getSiteConsultingRate = (s) => {
    if (s
        && s.site_service_modules
        && s.site_service_modules.consulting
        && s.site_service_modules.consulting.general_consulting
        && s.site_service_modules.consulting.general_consulting.pricing)
        return s.site_service_modules.consulting.general_consulting.pricing.default;
    else return 0;
  }

  const getCoveredHours = () => {
    if (!subscription || !subscription.site_package || !subscription.site_package.support_product) return 0;
    const { is_active, included_support_hours } = subscription.site_package.support_product;
    if (!is_active) return 0;
    return included_support_hours;
  }

    const hasSupportPlan = () => {
        if (!subscription || !subscription.site_package || !subscription.site_package.support_product.name) return false;
        return subscription.site_package.support_product.is_active;
    }

    const getSupportPlanName = () => {
        if (!subscription || !subscription.site_package) return false;
        return subscription.site_package.support_product.name;
    }

    const getSupportPlanDescription = () => {
        if (!subscription || !subscription.site_package) return false;
        return subscription.site_package.support_product.description;
    }

    const renderSupportPlanDetails = () => {
        if (!hasSupportPlan()) {
            return (<p>This account doesn’t currently have a BCK Support Plan. While one-time support is available, having a plan ensures faster response times, discounted rates, and priority service. Let us know if you'd like to explore your options.</p>)
        }
        return (<>
            <p>You currently have the <strong>{getSupportPlanName()}</strong>. This plan comes with the following perks:</p>
            {renderPerksSection()}

        </>
        )
    }

    const renderPerksSection = () => {
        if (!hasSupportPlan()) return null;
      
        const perks = subscription.site_package.support_product.perks;
        if (!perks || perks.length === 0) return null;
      
        return (
          <ul style={{ paddingLeft: '20px', marginTop: 2 }}>
            {perks.map((perk) => (
              <li key={perk.name} style={{ marginBottom: '8px', fontSize: '16px' }}>
                {perk.name}
              </li>
            ))}
          </ul>
        );
      };

  const renderCycleFooter = (data) => {
    const totalHours = data && data.time_utilized && data.time_utilized.total_time_hours
      ? parseFloat(data.time_utilized.total_time_hours)
      : 0.0;
  
    const rate = getSiteConsultingRate(site);
    const included = getCoveredHours();
    const overage = Math.max(0, totalHours - included);
    const amountDue = ((overage * rate) / 100).toFixed(2);
    const billDate = data && data.time_period && data.time_period.end
      ? new Date(new Date(data.time_period.end).getTime() + (10 * 24 * 60 * 60 * 1000))
      : null;
  
    const rowStyle = {
      display: 'flex',
      justifyContent: 'space-between',
      padding: '12px 16px',
      borderBottom: '1px solid #e0e0e0',
      fontSize: '16px'
    };
  
    const labelStyle = { color: '#444' };
    const valueStyle = { fontWeight: 500 };
  
    return (
      <Box sx={{ marginTop: 4, width: '100%' }}>
        <Paper
          variant="outlined"
          sx={{overflow: 'hidden' }}
        >
          <Box sx={{ padding: '16px 16px 0' }}>
            <h4 style={{ marginTop: 0, marginBottom: '5px', fontSize: '24px' }}>Billing Summary</h4>
          </Box>
  
          <Box sx={rowStyle}>
            <span style={labelStyle}>Hours Covered</span>
            <span style={valueStyle}>{included.toFixed(1)} hrs</span>
          </Box>
  
          <Box sx={rowStyle}>
            <span style={labelStyle}>Hours Billed</span>
            <span style={valueStyle}>{overage.toFixed(2)} hrs</span>
          </Box>
  
          <Box sx={rowStyle}>
            <span style={labelStyle}>Hourly Rate</span>
            <span style={valueStyle}>${(rate/100).toFixed(2)}/hr</span>
          </Box>
  
          <Box sx={{
            ...rowStyle,
            backgroundColor: '#f8f8f8',
            borderBottom: 'none',
            fontSize: '18px'
          }}>
            <span style={{ fontWeight: 'bold' }}>Total Amount Due</span>
            <span style={{ fontWeight: 'bold' }}>${amountDue}</span>
          </Box>
        </Paper>
        <p style={{ textAlign: 'center' }} >If there is any amount due, your account will be automatically charged 10 days after the cycle ends (on  {billDate ? billDate.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' }) : '-'}).</p>
      </Box>
    );
  };
  
  
  

  const renderRequestsList = (requests) => (
    <Paper variant="outlined" sx={{ marginTop: '5px', paddingLeft: '10px', paddingRight: '10px' }}>
      <List style={{ flex: 1 }}>
        {requests.map((submission, index) => (
          <ListItem
            key={submission.submission_id}
            sx={{
                backgroundColor: '#fff',
                marginBottom: '5px',
                borderBottom:
                    index !== requests.length - 1
                        ? '1px solid #e0e0e0'
                        : 'none',
            }}>
            <ListItemAvatar><StyledStatusIcon status={submission.submission_status} /></ListItemAvatar>
            <ListItemText
              primary={submission.submission_fields && submission.submission_fields.title ? submission.submission_fields.title : 'Untitled Request'}
              secondary={`Closed on ${formatDateToFancyString(submission.submission_closed_epoch)}`}
            />
            <ListItemSecondaryAction>
              <TooltipIconButton
                text="View Details"
                icon={<AssignmentOutlined sx={{ color: Colors.primaryColor }} />}
                onClick={() => setRequestInView({
                  ...submission,
                  submission_hours: submission.submission_time_entries,
                })}
              />
            </ListItemSecondaryAction>
          </ListItem>
        ))}
      </List>
    </Paper>
  );

  const renderHistory = () => {
    const renderCycleLabel = (offset) => {
        if (!hoursReport || !hoursReport.time_period || !hoursReport.time_period.end) return '';
        
        const baseDate = new Date(hoursReport.time_period.end);
        const adjustedDate = new Date(baseDate);
        adjustedDate.setMonth(baseDate.getMonth() + offset); // offset is negative (e.g., -2, -3)
      
        return `${adjustedDate.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })}`;
      };
    
      const historyOptions = Array.from({ length: 6 }, (_, i) => -2 - i); // [-2, -3, -4, -5, -6, -7]
    
      const requests = historyReport && Array.isArray(historyReport.tracked_completed_requests)
        ? historyReport.tracked_completed_requests
        : [];
    
      const renderDropDown = () => (
        <StyledDropDown
        fullWidth
            label="Cycle"
            value={selectedOffset}
            onChange={(e) => setSelectedOffset(parseInt(e.target.value))}
        >
            <MenuItem
                key={0}
                value={0}
            >
                <em>No cycle selected</em>
            </MenuItem>
                {historyOptions.map((offset) => (
                <MenuItem
                key={offset}
                value={offset}
            >
                {renderCycleLabel(offset)}
            </MenuItem>
        ))}
        </StyledDropDown>
      )

      return (
        <StyledSection title="Completed Request History">
            { isLoading ? <StyledProgressCircle />
            :
            <>
                <Box>
                    <Box sx={{ marginBottom: 2 }}>
                        {renderDropDown()}
                    </Box>
                    {isLoadingHistory ? (
                        <StyledProgressCircle />
                    ) : requests.length > 0 ? (
                        <>
                            {renderRequestsList(requests)}
                            <p style={{ textAlign: 'center' }} >If you are looking for the billing summary for this cycle, you can find in your billing section of your Account.</p>
                        </>
                        
                    ) : (
                        <p style={{ textAlign: 'center' }}>
                        {selectedOffset === 0 ? 'Your completed request history will appear here. If you are looking for a request older than 8 months, please contact us.' : 'No completed requests found for this cycle.'}
                        </p>
                    )}
                </Box>
            </>    
        
        }
        </StyledSection>
      )
  }
  const renderFAQ = () => (
    <StyledSection title="Frequently Asked Questions">
      {[{
        q: "What is included in my support plan?",
        a: renderSupportPlanDetails()
      }, {
        q: "How are hours tracked?",
        a: "All support hours are tracked using a secure third-party timekeeping system. Each task is logged externally with precise time entries, and those records are automatically reported back to BCK. This ensures transparency, accuracy, and accountability in how your support time is measured."
      }, {
        q: "When will I be billed?",
        a: "Billing occurs 10 days after each cycle ends for any time beyond your included hours."
      }, {
        q: "Can I upgrade my plan?",
        a: "Yes, reach out through your dashboard or contact support to discuss options."
      }, {
        q: "What if I don't use all my hours?",
        a: "Any included support hours that are not used within a billing cycle do not carry over to the next month. To get the most value from your plan, we recommend using your hours each month for updates, improvements, or support."
      }].map((item, i) => (
        <StyledAccordionItem key={i} title={item.q} details={item.a} />
      ))}
    </StyledSection>
  );

  const currentRequests = (hoursReport && Array.isArray(hoursReport.tracked_completed_requests)) ? hoursReport.tracked_completed_requests : [];
  const prevRequests = (prevCycleReport && Array.isArray(prevCycleReport.tracked_completed_requests)) ? prevCycleReport.tracked_completed_requests : [];

  return (
    <StyledPage>
      <Grid container spacing={2}>
        <Grid item xs={12}><StyledSubHeader title="Support Completed" /></Grid>
        <Grid item xs={12}>
          <StyledSection title="Current Cycle">
            {isLoading ? <StyledProgressCircle /> : (
              <>
                {renderCycleHeader(hoursReport)}
                {currentRequests.length > 0 ? renderRequestsList(currentRequests) : <p style={{ padding: '16px' }}>No completed requests.</p>}
              </>
            )}
          </StyledSection>
        </Grid>
        <Grid item xs={12}>
          <StyledSection title="Previous Cycle">
            {isLoading ? <StyledProgressCircle /> : (
              <>
                {renderCycleHeader(prevCycleReport)}
                {prevRequests.length > 0 ? renderRequestsList(prevRequests) : <p style={{ padding: '16px' }}>No completed requests.</p>}
                {renderCycleFooter(prevCycleReport)}
              </>
            )}
          </StyledSection>
        </Grid>
        <Grid item xs={12}>{renderHistory()}</Grid>
        <Grid item xs={12}>{renderFAQ()}</Grid>
      </Grid>
      <ViewRequestModal
        open={!!requestInView}
        onClose={() => setRequestInView(null)}
        request={requestInView}
      />
    </StyledPage>
  );
};

export default observer(withRouter(SupportCompletedPage));
